import React, { useMemo, useState } from 'react';
import { X, CornerUpLeft, CornerUpRight, ArrowUp, MapPin, Flag } from 'lucide-react';

/**
 * In-App Navigation Overlay for riders during an in-progress trip.
 *
 * Renders a Bolt/Uber-style top HUD with the next maneuver + distance,
 * an ETA / remaining-distance strip, and a full step list in a
 * bottom sheet. Does NOT open external maps.
 *
 * Props:
 *  - directions:   google.maps.DirectionsResult (already populated by the
 *                  parent component's DirectionsService call)
 *  - pickupName:   optional string, destination label
 *  - dropoffName:  string — where we are heading
 *  - onExit:       () => void — closes the in-app navigation mode
 */
const InAppNavigationView = ({ directions, dropoffName, onExit }) => {
  const [expanded, setExpanded] = useState(false);

  const leg = directions?.routes?.[0]?.legs?.[0];
  const steps = leg?.steps || [];
  const totalEta = leg?.duration?.text || '—';
  const totalDist = leg?.distance?.text || '—';

  // Current step = first one with remaining distance > 0. Since we don't track
  // the rider's live driving progress against maneuvers, we surface the next
  // instruction and let the rider follow along visually.
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const currentStep = steps[currentStepIdx] || steps[0];

  const maneuverIcon = useMemo(() => {
    const m = (currentStep?.maneuver || '').toLowerCase();
    if (m.includes('left')) return <CornerUpLeft className="w-8 h-8" />;
    if (m.includes('right')) return <CornerUpRight className="w-8 h-8" />;
    return <ArrowUp className="w-8 h-8" />;
  }, [currentStep]);

  // Convert instructions HTML -> plain text
  const plainInstruction = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  if (!directions) {
    return (
      <div
        data-testid="inapp-nav-empty"
        className="fixed inset-0 z-[65] bg-gray-950/70 flex items-center justify-center p-6"
      >
        <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl">
          <p className="text-gray-800 font-semibold mb-2">Getting directions...</p>
          <p className="text-sm text-gray-500 mb-5">
            We're calculating your route. One moment.
          </p>
          <button
            onClick={onExit}
            data-testid="inapp-nav-close-empty"
            className="w-full bg-gray-900 hover:bg-black text-white rounded-full py-3 font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="inapp-nav-overlay" className="fixed inset-0 z-[60] pointer-events-none">
      {/* Top HUD — next maneuver */}
      <div className="pointer-events-auto absolute top-0 left-0 right-0 bg-emerald-600 text-white px-4 pt-[calc(env(safe-area-inset-top,0)+14px)] pb-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div
            className="w-14 h-14 rounded-2xl bg-white/15 ring-1 ring-white/20 flex items-center justify-center flex-shrink-0"
            data-testid="inapp-nav-maneuver"
          >
            {maneuverIcon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-emerald-100/80 font-semibold">
              In {currentStep?.distance?.text || '—'}
            </div>
            <div
              className="text-[17px] font-semibold leading-tight mt-0.5 line-clamp-2"
              data-testid="inapp-nav-instruction"
            >
              {plainInstruction(currentStep?.instructions) || 'Head to destination'}
            </div>
          </div>
          <button
            type="button"
            onClick={onExit}
            data-testid="inapp-nav-exit"
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center flex-shrink-0 transition-colors"
            aria-label="Exit navigation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Bottom ETA / remaining bar */}
      <div
        className="pointer-events-auto absolute left-3 right-3 bg-white rounded-2xl shadow-2xl px-4 py-3 flex items-center justify-between gap-3"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0) + 18px)' }}
        data-testid="inapp-nav-eta-bar"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <Flag className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
              Arriving at
            </div>
            <div className="text-[14px] font-semibold text-gray-900 truncate" data-testid="inapp-nav-destination">
              {dropoffName || 'Destination'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 pl-3 border-l border-gray-200">
          <div className="text-right">
            <div className="text-[18px] font-bold tabular-nums text-gray-900 leading-none" data-testid="inapp-nav-eta">
              {totalEta}
            </div>
            <div className="text-[11px] text-gray-500 tabular-nums" data-testid="inapp-nav-distance">
              {totalDist}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="inapp-nav-toggle-steps"
            className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline"
          >
            {expanded ? 'Hide' : 'Steps'}
          </button>
        </div>
      </div>

      {/* Expandable steps list */}
      {expanded && (
        <div
          className="pointer-events-auto absolute left-3 right-3 bg-white rounded-2xl shadow-2xl max-h-[55vh] overflow-y-auto"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0) + 92px)' }}
          data-testid="inapp-nav-steps-list"
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
            <span className="text-[13px] font-bold text-gray-900 uppercase tracking-wide">
              Turn-by-turn
            </span>
            <span className="text-[11px] text-gray-500 tabular-nums">
              {steps.length} step{steps.length === 1 ? '' : 's'}
            </span>
          </div>
          <ol className="divide-y divide-gray-100">
            {steps.map((s, i) => {
              const active = i === currentStepIdx;
              const m = (s.maneuver || '').toLowerCase();
              const Icon = m.includes('left')
                ? CornerUpLeft
                : m.includes('right')
                ? CornerUpRight
                : i === steps.length - 1
                ? MapPin
                : ArrowUp;
              return (
                <li
                  key={i}
                  onClick={() => setCurrentStepIdx(i)}
                  className={`px-4 py-3 flex items-start gap-3 cursor-pointer transition-colors ${
                    active ? 'bg-emerald-50' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`inapp-nav-step-${i}`}
                >
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      active ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] text-gray-900 leading-snug">
                      {plainInstruction(s.instructions)}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5 tabular-nums">
                      {s.distance?.text} · {s.duration?.text}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
};

export default InAppNavigationView;
